
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Facebook, Twitter, Linkedin, Copy, Check, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import type { Post } from '@/hooks/posts/types';

interface SocialShareDialogProps {
  post: Post;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SocialShareDialog = ({ post, open, onOpenChange }: SocialShareDialogProps) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/post/${post.id}`;
  const shareText = `Check out this post about ${post.crop}: ${post.content.slice(0, 100)}...`;

  const handleCopyLink = async () => {
    try {
      console.log('Attempting to copy link:', shareUrl);
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
      console.log('Link successfully copied to clipboard');
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback for browsers that don't support clipboard API
      try {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
        console.log('Link copied using fallback method');
      } catch (fallbackErr) {
        console.error('Fallback copy method also failed:', fallbackErr);
        toast.error('Failed to copy link');
      }
    }
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const handleLinkedInShare = () => {
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedinUrl, '_blank', 'width=600,height=400');
  };

  const handleInstagramShare = async () => {
    console.log('Instagram share button clicked');
    try {
      // Instagram doesn't have a direct share URL, so we'll copy the link and notify the user
      await handleCopyLink();
      toast.info('Link copied! Open Instagram app and paste the link in your story or post.');
      console.log('Instagram share process completed successfully');
    } catch (error) {
      console.error('Instagram share failed:', error);
      toast.error('Failed to prepare Instagram share');
    }
  };

  const handleTikTokShare = async () => {
    console.log('TikTok share button clicked');
    try {
      // TikTok doesn't have a direct web share URL, so we'll copy the link and notify the user
      await handleCopyLink();
      toast.info('Link copied! Open TikTok app and paste the link in your video description.');
      console.log('TikTok share process completed successfully');
    } catch (error) {
      console.error('TikTok share failed:', error);
      toast.error('Failed to prepare TikTok share');
    }
  };

  // Custom TikTok icon component since it's not available in lucide-react
  const TikTokIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M19.321 5.562a5.124 5.124 0 0 1-.443-.258 6.228 6.228 0 0 1-1.137-.966c-.849-.952-1.265-2.067-1.265-3.394V.5h-3.255v15.32c0 1.765-1.432 3.195-3.197 3.195s-3.197-1.43-3.197-3.195c0-1.766 1.432-3.196 3.197-3.196.177 0 .35.014.518.04V9.368c-.168-.023-.34-.035-.518-.035C4.486 9.333 0 13.818 0 19.354c0 5.537 4.486 10.021 10.021 10.021s10.021-4.484 10.021-10.021v-7.09c1.265.74 2.71 1.132 4.229 1.132V9.898c-1.789 0-3.438-.64-4.72-1.704l-.23-.632z"/>
    </svg>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby="share-desc">
        <DialogHeader>
          <DialogTitle>Share Post</DialogTitle>
        </DialogHeader>
        
        <p id="share-desc" className="sr-only">
          Share this agricultural post with your network through various social media platforms and messaging apps.
        </p>
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-sm text-gray-700 mb-2">Post Preview</h4>
            <p className="text-sm text-gray-600 line-clamp-3">{post.content}</p>
            <p className="text-xs text-gray-500 mt-2">By {post.user.name} • {post.crop}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={handleFacebookShare}
              className="flex items-center gap-2"
            >
              <Facebook size={16} className="text-blue-600" />
              Facebook
            </Button>
            
            <Button
              variant="outline"
              onClick={handleTwitterShare}
              className="flex items-center gap-2"
            >
              <Twitter size={16} className="text-blue-400" />
              Twitter
            </Button>
            
            <Button
              variant="outline"
              onClick={handleLinkedInShare}
              className="flex items-center gap-2"
            >
              <Linkedin size={16} className="text-blue-700" />
              LinkedIn
            </Button>
            
            <Button
              variant="outline"
              onClick={handleInstagramShare}
              className="flex items-center gap-2"
            >
              <Instagram size={16} className="text-pink-600" />
              Instagram
            </Button>
            
            <Button
              variant="outline"
              onClick={handleTikTokShare}
              className="flex items-center gap-2"
            >
              <TikTokIcon size={16} className="text-black" />
              TikTok
            </Button>
            
            <Button
              variant="outline"
              onClick={handleCopyLink}
              className="flex items-center gap-2"
            >
              {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
